import React, { useState, useEffect, useCallback } from 'react';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Plus, Trash2, Save, ChevronDown, ChevronUp, Clock, DollarSign, Truck, User, FileDown, CalendarPlus, Fuel, Car, Send } from 'lucide-react';
import { toast } from 'sonner';
import SmartAlertsPanel from './SmartAlertsPanel';
import { SwissCard, SectionHeader, KpiCard, Overline } from '../layout/SwissUI';

const API = process.env.REACT_APP_BACKEND_URL;
const fmt = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
const p$ = (s) => parseFloat(String(s || '0').replace(/[$,]/g, '')) || 0;

const SERVICE_TYPES = [
  { code: 'AT', label: 'Airport Transfer' }, { code: 'PL', label: 'Point to Lux' },
  { code: 'CC', label: 'Chauffeur Concierge' }, { code: 'EX', label: 'Exotic Driven' },
  { code: 'GR', label: 'Group / Sprinter' }, { code: 'WD', label: 'Wedding' },
  { code: 'CO', label: 'Corporate' }, { code: 'EV', label: 'Event' }, { code: 'DR', label: 'Driving Rental' },
];

const VEHICLE_CATS = ['Sedan', 'SUV', 'Sprinter/Van', 'Specialty/Exotic', 'Group/Bus', 'Client Vehicle'];
const EXPENSE_ITEMS = [
  { key: 'fuel', label: 'Fuel', icon: '⛽' }, { key: 'tolls', label: 'Tolls', icon: '🛣' },
  { key: 'cleaning', label: 'Cleaning', icon: '🧹' }, { key: 'vehicle_rental', label: 'Vehicle Rental', icon: '🚗' },
  { key: 'misc', label: 'Misc/Other', icon: '📋' },
];

const BookingCard = ({ bk, idx, onChange, onRemove }) => {
  const [expanded, setExpanded] = useState(false);
  const u = (field, val) => onChange(idx, field, val);
  const gross = p$(bk.hours) * p$(bk.rate);
  const driverPay = p$(bk.driver_hours || bk.hours) * p$(bk.driver_rate);

  return (
    <SwissCard className="overflow-hidden" testid={`booking-card-${idx}`}>
      <div className="bg-gray-950 text-white px-4 py-2 flex items-center justify-between">
        <span className="text-xs font-bold">Booking #{idx + 1}</span>
        <div className="flex items-center gap-2">
          <select value={bk.status || 'confirmed'} onChange={e => u('status', e.target.value)} className="bg-transparent border border-white/20 rounded text-[10px] px-2 py-0.5">
            <option value="confirmed">Confirmed</option><option value="completed">Completed</option>
            <option value="pending">Pending</option><option value="cancelled">Cancelled</option>
          </select>
          <button onClick={() => onRemove(idx)} className="text-red-400 hover:text-red-300"><Trash2 className="h-3.5 w-3.5" /></button>
        </div>
      </div>
      <div className="p-4 space-y-3">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          <div><label className="text-[9px] text-gray-500 uppercase font-bold">Service Type</label>
            <select value={bk.service_type || ''} onChange={e => u('service_type', e.target.value)} className="w-full h-8 text-xs border rounded px-2 border-gray-200">
              <option value="">Select...</option>{SERVICE_TYPES.map(s => <option key={s.code} value={s.code}>{s.code} — {s.label}</option>)}
            </select></div>
          <div><label className="text-[9px] text-gray-500 uppercase font-bold">Vehicle</label>
            <Input value={bk.vehicle || ''} onChange={e => u('vehicle', e.target.value)} placeholder="e.g. Escalade ESV" className="h-8 text-xs" /></div>
          <div><label className="text-[9px] text-gray-500 uppercase font-bold">Client</label>
            <Input value={bk.client || ''} onChange={e => u('client', e.target.value)} placeholder="Client name" className="h-8 text-xs" /></div>
          <div><label className="text-[9px] text-gray-500 uppercase font-bold">Driver</label>
            <Input value={bk.driver || ''} onChange={e => u('driver', e.target.value)} placeholder="Driver name" className="h-8 text-xs" /></div>
        </div>
        <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
          <div><label className="text-[9px] text-gray-500 uppercase font-bold">Hours</label>
            <Input type="number" value={bk.hours || ''} onChange={e => u('hours', e.target.value)} placeholder="0" className="h-8 text-xs" /></div>
          <div><label className="text-[9px] text-gray-500 uppercase font-bold">Rate/hr</label>
            <Input value={bk.rate || ''} onChange={e => u('rate', e.target.value)} placeholder="$0" className="h-8 text-xs" /></div>
          <div><label className="text-[9px] text-gray-500 uppercase font-bold">Gross</label>
            <div className="h-8 flex items-center text-xs font-bold text-red-600">{fmt(gross)}</div></div>
          <div><label className="text-[9px] text-gray-500 uppercase font-bold">Driver Rate</label>
            <Input value={bk.driver_rate || ''} onChange={e => u('driver_rate', e.target.value)} placeholder="$35" className="h-8 text-xs" /></div>
          <div><label className="text-[9px] text-gray-500 uppercase font-bold">Driver Pay</label>
            <div className="h-8 flex items-center text-xs font-semibold text-gray-600">{fmt(driverPay)}</div></div>
          <div><label className="text-[9px] text-gray-500 uppercase font-bold">Collected</label>
            <Input value={bk.collected || ''} onChange={e => u('collected', e.target.value)} placeholder="$0" className="h-8 text-xs" /></div>
        </div>
        <button onClick={() => setExpanded(!expanded)} className="w-full flex items-center justify-center gap-1 text-[10px] text-gray-400 hover:text-gray-600 pt-1">
          {expanded ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />} {expanded ? 'Less' : 'More Details'}
        </button>
        {expanded && (
          <div className="space-y-3 pt-2 border-t border-gray-100">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              <div><label className="text-[9px] text-gray-500 uppercase font-bold">Phone</label>
                <Input value={bk.phone || ''} onChange={e => u('phone', e.target.value)} placeholder="Client phone" className="h-8 text-xs" /></div>
              <div><label className="text-[9px] text-gray-500 uppercase font-bold">Invoice #</label>
                <Input value={bk.invoice || ''} onChange={e => u('invoice', e.target.value)} placeholder="INV-" className="h-8 text-xs" /></div>
              <div><label className="text-[9px] text-gray-500 uppercase font-bold">Mileage</label>
                <Input type="number" value={bk.mileage || ''} onChange={e => u('mileage', e.target.value)} placeholder="0" className="h-8 text-xs" /></div>
              <div><label className="text-[9px] text-gray-500 uppercase font-bold">Tip</label>
                <Input value={bk.tip || ''} onChange={e => u('tip', e.target.value)} placeholder="$0" className="h-8 text-xs" /></div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div><label className="text-[9px] text-gray-500 uppercase font-bold">Pickup Location</label>
                <Input value={bk.pickup || ''} onChange={e => u('pickup', e.target.value)} placeholder="Pickup address" className="h-8 text-xs" /></div>
              <div><label className="text-[9px] text-gray-500 uppercase font-bold">Dropoff Location</label>
                <Input value={bk.dropoff || ''} onChange={e => u('dropoff', e.target.value)} placeholder="Dropoff address" className="h-8 text-xs" /></div>
            </div>
            <div><label className="text-[9px] text-gray-500 uppercase font-bold">Notes</label>
              <Input value={bk.notes || ''} onChange={e => u('notes', e.target.value)} placeholder="Special requests, details..." className="h-8 text-xs" /></div>
            <div><label className="text-[9px] text-gray-500 uppercase font-bold">Invoice Status</label>
              <div className="flex gap-1 mt-1">{['Sent', 'Viewed', 'Paid', 'Overdue'].map(s => (
                <button key={s} onClick={() => u('inv_status', s)} className={`px-2 py-1 rounded text-[9px] font-bold border ${bk.inv_status === s ? (s === 'Paid' ? 'bg-emerald-100 text-emerald-700 border-emerald-300' : s === 'Overdue' ? 'bg-red-100 text-red-700 border-red-300' : 'bg-blue-100 text-blue-700 border-blue-300') : 'border-gray-200 text-gray-500'}`}>{s}</button>
              ))}</div></div>
          </div>
        )}
      </div>
    </SwissCard>
  );
};

const DailyOpsTab = () => {
  const today = new Date().toISOString().split('T')[0];
  const [dateKey, setDateKey] = useState(today);
  const [bookings, setBookings] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [vehicles, setVehicles] = useState(VEHICLE_CATS.map(c => ({ category: c, runs: 0, hours: 0, gross: '', notes: '' })));
  const [expenses, setExpenses] = useState({});
  const [totalMiles, setTotalMiles] = useState('');
  const [arTotal, setArTotal] = useState('');
  const [openItems, setOpenItems] = useState(['', '', '', '']);
  const [saving, setSaving] = useState(false);
  const [savingDeployment, setSavingDeployment] = useState(false);
  const [pushingPayroll, setPushingPayroll] = useState(false);
  const [rosterDrivers, setRosterDrivers] = useState([]);  // real drivers from /api/command-center/drivers

  // Calculations
  const grossRevenue = bookings.reduce((s, b) => s + (p$(b.hours) * p$(b.rate)), 0);
  const driverCost = bookings.reduce((s, b) => s + (p$(b.driver_hours || b.hours) * p$(b.driver_rate)), 0);
  const collected = bookings.reduce((s, b) => s + p$(b.collected), 0);
  const totalExpenses = EXPENSE_ITEMS.reduce((s, e) => s + p$(expenses[e.key]), 0);
  const netToAce = grossRevenue - driverCost - totalExpenses;
  const taxReserve = Math.round(netToAce * 0.30);
  const takeHome = netToAce - taxReserve;
  const mileDeduction = Math.round(p$(totalMiles) * 0.70);
  const arBalance = p$(arTotal) - collected;

  const load = useCallback(async () => {
    try {
      const r = await fetch(`${API}/api/command-center/ops/daily/${dateKey}`);
      const d = await r.json();
      if (d.bookings?.length) setBookings(d.bookings);
      if (d.drivers?.length) setDrivers(d.drivers);
      if (d.vehicles?.length) setVehicles(d.vehicles);
      if (d.expenses) setExpenses(d.expenses);
      if (d.total_miles) setTotalMiles(d.total_miles);
      if (d.ar_total) setArTotal(d.ar_total);
      if (d.open_items) setOpenItems(d.open_items);
    } catch {}
  }, [dateKey]);

  useEffect(() => { load(); }, [load]);

  // Load real driver roster (for the deployment dropdown + auto-fill of rates)
  useEffect(() => {
    fetch(`${API}/api/command-center/drivers`).then(r => r.json()).then(d => setRosterDrivers(d.drivers || [])).catch(() => {});
  }, []);

  // Compute hours from start/end on the fly
  const computeHours = (start, end) => {
    if (!start || !end) return 0;
    const [sh, sm] = start.split(':').map(Number);
    const [eh, em] = end.split(':').map(Number);
    if (Number.isNaN(sh) || Number.isNaN(eh)) return 0;
    let diff = (eh * 60 + (em || 0)) - (sh * 60 + (sm || 0));
    if (diff < 0) diff += 24 * 60;  // overnight
    return Math.round((diff / 60) * 100) / 100;
  };

  const addDriverRow = () => setDrivers(p => [...p, {
    driver_id: '', name: '', runs: 0, start_time: '', end_time: '', hours: 0, rate: '35', owed: '', note: '',
  }]);

  const removeDriverRow = (i) => setDrivers(p => p.filter((_, j) => j !== i));

  const pickDriver = (idx, driverId) => {
    const d = rosterDrivers.find(x => x.driver_id === driverId);
    setDrivers(p => p.map((row, j) => j === idx ? {
      ...row,
      driver_id: driverId,
      name: d?.name || row.name,
      rate: String(d?.hourly_rate || d?.rate || row.rate || '35'),
    } : row));
  };

  const updateDriverField = (i, field, val) => {
    setDrivers(p => p.map((row, j) => {
      if (j !== i) return row;
      const next = { ...row, [field]: val };
      // Auto-recompute hours when start/end changes
      if (field === 'start_time' || field === 'end_time') {
        next.hours = computeHours(field === 'start_time' ? val : row.start_time, field === 'end_time' ? val : row.end_time);
      }
      return next;
    }));
  };

  // Section-level save (also bumps the daily ops doc so summary stays correct)
  const saveDeployment = async () => {
    setSavingDeployment(true);
    try {
      const r = await fetch(`${API}/api/command-center/ops/daily/${dateKey}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ bookings, drivers, vehicles, expenses, total_miles: totalMiles, ar_total: arTotal, open_items: openItems,
          summary: { gross: grossRevenue, driver_cost: driverCost, collected, expenses: totalExpenses, net: netToAce, tax_reserve: taxReserve, take_home: takeHome, mileage_deduction: mileDeduction, ar_balance: arBalance, booking_count: bookings.length }
        }),
      });
      if (!r.ok) throw new Error('Save failed');
      toast.success('Driver deployment saved');
    } catch (e) { toast.error(e.message); }
    setSavingDeployment(false);
  };

  const pushToPayroll = async () => {
    const eligible = drivers.filter(d => d.driver_id && d.start_time && d.end_time);
    if (!eligible.length) { toast.error('Add roster drivers with start & end times first'); return; }
    setPushingPayroll(true);
    try {
      const r = await fetch(`${API}/api/drivers/deployment/push-to-payroll`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ date_key: dateKey, drivers }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || 'Push failed');
      if (d.created > 0) toast.success(`${d.created} shift${d.created !== 1 ? 's' : ''} pushed to payroll${d.skipped ? ` · ${d.skipped} skipped (already pushed or incomplete)` : ''}`);
      else toast.info(d.skipped ? 'All shifts already pushed for this date' : 'Nothing to push');
    } catch (e) { toast.error(e.message); }
    setPushingPayroll(false);
  };

  const save = async () => {
    setSaving(true);
    try {
      await fetch(`${API}/api/command-center/ops/daily/${dateKey}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ bookings, drivers, vehicles, expenses, total_miles: totalMiles, ar_total: arTotal, open_items: openItems,
          summary: { gross: grossRevenue, driver_cost: driverCost, collected, expenses: totalExpenses, net: netToAce, tax_reserve: taxReserve, take_home: takeHome, mileage_deduction: mileDeduction, ar_balance: arBalance, booking_count: bookings.length }
        }),
      });
      toast.success('Daily log saved');
    } catch { toast.error('Failed to save'); }
    setSaving(false);
  };

  const addBooking = () => setBookings(p => [...p, { service_type: '', vehicle: '', client: '', driver: '', hours: '', rate: '', driver_rate: '35', collected: '', status: 'confirmed' }]);
  const removeBooking = (i) => setBookings(p => p.filter((_, j) => j !== i));
  const updateBooking = (i, field, val) => setBookings(p => p.map((b, j) => j === i ? { ...b, [field]: val } : b));

  const syncFromBookings = async () => {
    try {
      const r = await fetch(`${API}/api/command-center/ops/daily/${dateKey}/sync`);
      const d = await r.json();
      if (d.synced > 0) { toast.success(`Synced ${d.synced} bookings from system`); load(); }
      else { toast.info('No bookings found for this date'); }
    } catch { toast.error('Sync failed'); }
  };
  const updateVehicle = (i, field, val) => setVehicles(p => p.map((v, j) => j === i ? { ...v, [field]: val } : v));

  return (
    <div className="space-y-5" data-testid="daily-ops-tab">
      <SmartAlertsPanel />

      {/* Header */}
      <SectionHeader
        overline="Fill at end of each day"
        title="Daily Ops Log"
        action={
          <div className="flex items-center gap-2">
            <Input type="date" value={dateKey} onChange={e => setDateKey(e.target.value)} className="h-9 text-xs w-40" data-testid="daily-date" />
            <button onClick={save} disabled={saving} className="h-9 px-4 bg-[#E52B20] hover:bg-[#C92218] text-white text-xs font-bold uppercase tracking-wider flex items-center gap-1 disabled:opacity-50" data-testid="daily-save">
              <Save className="h-3 w-3" /> {saving ? 'Saving...' : 'Save'}
            </button>
          </div>
        }
      />

      {/* Summary Bar */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
        {[
          { label: 'Bookings', value: bookings.length, tone: 'default' },
          { label: 'Gross Revenue', value: fmt(grossRevenue), tone: 'default' },
          { label: 'Driver Cost', value: fmt(driverCost), tone: 'warn' },
          { label: 'Collected', value: fmt(collected), tone: 'default' },
          { label: 'Net to ACE', value: fmt(netToAce), tone: netToAce >= 0 ? 'success' : 'danger' },
        ].map((s, i) => (
          <KpiCard key={i} label={s.label} value={s.value} tone={s.tone} testid={`daily-kpi-${i}`} />
        ))}
      </div>

      {/* Bookings */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-bold">Bookings</h3>
          <Button size="sm" onClick={addBooking} className="bg-black hover:bg-gray-800 text-xs h-7" data-testid="add-booking"><Plus className="h-3 w-3 mr-1" /> Add Booking</Button>
          <Button size="sm" variant="outline" onClick={syncFromBookings} className="text-xs h-7" data-testid="sync-bookings">Sync from System</Button>
        </div>
        <div className="space-y-3">
          {bookings.map((bk, i) => <BookingCard key={i} bk={bk} idx={i} onChange={updateBooking} onRemove={removeBooking} />)}
          {bookings.length === 0 && <SwissCard className="p-6 text-center"><p className="text-xs text-[#666]">No bookings yet. Tap "Add Booking" to start.</p></SwissCard>}
        </div>
      </div>

      {/* Driver Deployment */}
      <SwissCard className="p-4" testid="driver-deployment-card">
        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
          <h3 className="text-sm font-bold flex items-center gap-2"><User className="h-4 w-4 text-red-600" /> Driver Deployment</h3>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" onClick={addDriverRow} className="text-xs h-7" data-testid="add-driver-row"><Plus className="h-3 w-3 mr-1" /> Add Driver</Button>
            <Button size="sm" onClick={saveDeployment} disabled={savingDeployment} className="bg-red-600 hover:bg-red-700 text-xs h-7" data-testid="save-deployment"><Save className="h-3 w-3 mr-1" /> {savingDeployment ? 'Saving…' : 'Save Deployment'}</Button>
            <Button size="sm" onClick={pushToPayroll} disabled={pushingPayroll} className="bg-emerald-600 hover:bg-emerald-700 text-xs h-7" data-testid="push-to-payroll"><Send className="h-3 w-3 mr-1" /> {pushingPayroll ? 'Pushing…' : 'Push to Payroll'}</Button>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead><tr className="border-b border-gray-200">
              <th className="text-left py-2 text-[9px] text-gray-500 uppercase">Driver</th>
              <th className="text-center py-2 text-[9px] text-gray-500 uppercase w-24">Start</th>
              <th className="text-center py-2 text-[9px] text-gray-500 uppercase w-24">End</th>
              <th className="text-center py-2 text-[9px] text-gray-500 uppercase w-16">Hours</th>
              <th className="text-center py-2 text-[9px] text-gray-500 uppercase w-16">Runs</th>
              <th className="text-center py-2 text-[9px] text-gray-500 uppercase w-20">Rate/Hr</th>
              <th className="text-center py-2 text-[9px] text-gray-500 uppercase w-24">Pay (Auto)</th>
              <th className="text-left py-2 text-[9px] text-gray-500 uppercase">Notes</th>
              <th className="w-10"></th>
            </tr></thead>
            <tbody>
              {drivers.map((d, i) => {
                const pay = p$(d.hours) * p$(d.rate);
                return (
                  <tr key={i} className="border-b border-gray-100" data-testid={`deployment-row-${i}`}>
                    <td className="py-2 pr-2">
                      <select
                        value={d.driver_id || ''}
                        onChange={e => pickDriver(i, e.target.value)}
                        className="w-full h-7 text-xs border border-gray-200 rounded px-2 bg-white"
                        data-testid={`deployment-driver-${i}`}
                      >
                        <option value="">— pick driver —</option>
                        {rosterDrivers.map(r => <option key={r.driver_id} value={r.driver_id}>{r.name}</option>)}
                      </select>
                      {!d.driver_id && d.name && <Input value={d.name} onChange={e => updateDriverField(i, 'name', e.target.value)} className="h-6 text-[10px] mt-1 border-0 bg-gray-50" placeholder="or type a name (affiliate)" />}
                    </td>
                    <td className="py-2"><Input type="time" value={d.start_time || ''} onChange={e => updateDriverField(i, 'start_time', e.target.value)} className="h-7 text-xs text-center w-20" data-testid={`deployment-start-${i}`} /></td>
                    <td className="py-2"><Input type="time" value={d.end_time || ''} onChange={e => updateDriverField(i, 'end_time', e.target.value)} className="h-7 text-xs text-center w-20" data-testid={`deployment-end-${i}`} /></td>
                    <td className="py-2 text-center font-semibold">{Number(d.hours || 0).toFixed(2)}</td>
                    <td className="py-2"><Input type="number" value={d.runs || ''} onChange={e => updateDriverField(i, 'runs', e.target.value)} className="h-7 text-xs text-center w-14" /></td>
                    <td className="py-2"><Input value={d.rate || ''} onChange={e => updateDriverField(i, 'rate', e.target.value)} placeholder="$35" className="h-7 text-xs text-center w-16" /></td>
                    <td className="py-2 text-center font-semibold text-orange-600">{fmt(pay)}</td>
                    <td className="py-2"><Input value={d.note || ''} onChange={e => updateDriverField(i, 'note', e.target.value)} placeholder="Notes" className="h-7 text-xs border-0 bg-transparent p-0" /></td>
                    <td className="py-2 text-right"><button onClick={() => removeDriverRow(i)} className="text-red-500 hover:text-red-700"><Trash2 className="h-3 w-3" /></button></td>
                  </tr>
                );
              })}
              {drivers.length === 0 && (
                <tr><td colSpan="9" className="py-6 text-center text-xs text-gray-400 italic">No drivers deployed for this day. Click "Add Driver" to start.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </SwissCard>

      {/* Vehicle Utilization */}
      <SwissCard className="p-4">
        <h3 className="text-sm font-bold mb-3 flex items-center gap-2"><Car className="h-4 w-4 text-red-600" /> Vehicle Utilization</h3>
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead><tr className="border-b border-gray-200">
              <th className="text-left py-2 text-[9px] text-gray-500 uppercase">Vehicle</th>
              <th className="text-center py-2 text-[9px] text-gray-500 uppercase w-16">Runs</th>
              <th className="text-center py-2 text-[9px] text-gray-500 uppercase w-16">Hours</th>
              <th className="text-center py-2 text-[9px] text-gray-500 uppercase w-24">Gross</th>
              <th className="text-left py-2 text-[9px] text-gray-500 uppercase">Notes</th>
            </tr></thead>
            <tbody>
              {vehicles.map((v, i) => (
                <tr key={i} className="border-b border-gray-100">
                  <td className="py-2 font-semibold">{v.category}</td>
                  <td className="py-2"><Input type="number" value={v.runs} onChange={e => updateVehicle(i, 'runs', e.target.value)} className="h-7 text-xs text-center w-14" /></td>
                  <td className="py-2"><Input type="number" value={v.hours} onChange={e => updateVehicle(i, 'hours', e.target.value)} className="h-7 text-xs text-center w-14" /></td>
                  <td className="py-2"><Input value={v.gross} onChange={e => updateVehicle(i, 'gross', e.target.value)} placeholder="$0" className="h-7 text-xs text-center w-20" /></td>
                  <td className="py-2"><Input value={v.notes} onChange={e => updateVehicle(i, 'notes', e.target.value)} placeholder="Notes" className="h-7 text-xs border-0 bg-transparent p-0" /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </SwissCard>

      {/* Expenses */}
      <SwissCard className="p-4">
        <h3 className="text-sm font-bold mb-3">Expenses</h3>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
          {EXPENSE_ITEMS.map(e => (
            <div key={e.key} className="bg-gray-50 rounded-lg p-3">
              <p className="text-[9px] text-gray-500 uppercase font-bold mb-1">{e.icon} {e.label}</p>
              <Input value={expenses[e.key] || ''} onChange={ev => setExpenses(p => ({ ...p, [e.key]: ev.target.value }))} placeholder="$0" className="h-8 text-xs" />
            </div>
          ))}
          <div className="bg-gray-50 rounded-lg p-3">
            <p className="text-[9px] text-gray-500 uppercase font-bold mb-1">Total Miles</p>
            <Input type="number" value={totalMiles} onChange={e => setTotalMiles(e.target.value)} placeholder="0" className="h-8 text-xs" />
          </div>
        </div>
        <div className="flex items-center justify-between mt-3 pt-3 border-t border-gray-200">
          <span className="text-xs text-gray-500">Total Expenses</span><span className="text-sm font-bold text-red-600">{fmt(totalExpenses)}</span>
        </div>
      </SwissCard>

      {/* Daily Totals + Tax Reserve */}
      <div className="grid sm:grid-cols-2 gap-4">
        <SwissCard className="p-4 bg-[#0A0A0A] text-white">
          <h3 className="text-xs font-bold text-gray-400 uppercase mb-3">Daily Totals</h3>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between"><span className="text-gray-400">Gross Revenue</span><span className="font-bold">{fmt(grossRevenue)}</span></div>
            <div className="flex justify-between"><span className="text-gray-400">Driver Cost</span><span className="font-bold text-orange-400">{fmt(driverCost)}</span></div>
            <div className="flex justify-between"><span className="text-gray-400">Expenses</span><span className="font-bold text-red-400">{fmt(totalExpenses)}</span></div>
            <div className="flex justify-between border-t border-white/10 pt-2"><span className="text-white font-bold">Net to ACE</span><span className="font-black text-lg text-emerald-400">{fmt(netToAce)}</span></div>
          </div>
        </SwissCard>
        <SwissCard className="p-4 bg-[#0A0A0A] text-white">
          <h3 className="text-xs font-bold text-gray-400 uppercase mb-3">Tax Reserve + Mileage</h3>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between"><span className="text-gray-400">Net to ACE</span><span className="font-bold">{fmt(netToAce)}</span></div>
            <div className="flex justify-between"><span className="text-amber-400">Set Aside (30%)</span><span className="font-bold text-amber-400">{fmt(taxReserve)}</span></div>
            <div className="flex justify-between border-t border-white/10 pt-2"><span className="text-white font-bold">Take-Home</span><span className="font-black text-lg text-emerald-400">{fmt(takeHome)}</span></div>
            <div className="flex justify-between mt-2 pt-2 border-t border-white/10"><span className="text-gray-400">Mileage Deduction ({totalMiles || 0} mi × $0.70)</span><span className="font-bold text-blue-400">{fmt(mileDeduction)}</span></div>
          </div>
        </SwissCard>
      </div>

      {/* Accounts Receivable */}
      <SwissCard className="p-4">
        <h3 className="text-sm font-bold mb-3">Accounts Receivable</h3>
        <div className="grid grid-cols-3 gap-3 mb-3">
          <div><p className="text-[9px] text-gray-500 uppercase font-bold">Total AR Outstanding</p>
            <Input value={arTotal} onChange={e => setArTotal(e.target.value)} placeholder="$0" className="h-8 text-xs" /></div>
          <div><p className="text-[9px] text-gray-500 uppercase font-bold">Collected Today</p>
            <div className="h-8 flex items-center text-xs font-bold text-emerald-600">{fmt(collected)}</div></div>
          <div><p className="text-[9px] text-gray-500 uppercase font-bold">Updated AR Balance</p>
            <div className="h-8 flex items-center text-xs font-bold text-red-600">{fmt(arBalance)}</div></div>
        </div>
        <p className="text-[9px] text-gray-500 uppercase font-bold mb-2">Open Items</p>
        <div className="space-y-1">
          {openItems.map((item, i) => (
            <Input key={i} value={item} onChange={e => setOpenItems(p => p.map((v, j) => j === i ? e.target.value : v))} placeholder={`Open item ${i + 1}`} className="h-7 text-xs" />
          ))}
        </div>
      </SwissCard>

      {/* Floating Save */}
      <div className="sticky bottom-16 sm:bottom-4 z-20 flex justify-center">
        <Button onClick={save} disabled={saving} className="bg-red-600 hover:bg-red-700 shadow-xl text-sm h-10 px-8" data-testid="daily-save-float">
          <Save className="h-4 w-4 mr-2" /> {saving ? 'Saving...' : 'Save Daily Log'}
        </Button>
      </div>
    </div>
  );
};

export default DailyOpsTab;
